import { useEffect, useMemo, useRef, useState } from 'react'
import {
  api, ErroApi,
  type Lista, type Paciente, type Procedimento, type Profissional, type Recurso,
  type SessaoDisponivel,
} from '../lib/api'
import './novo-agendamento.css'

/**
 * Marcar atendimento.
 *
 * O formulário não pergunta o fim: ele vem da duração do procedimento.
 * Quem digita o fim à mão erra, e a agenda fica cheia de buracos de
 * cinco minutos que nunca são vendidos.
 *
 * E o conflito de horário NÃO é checado aqui. Uma consulta de "está
 * livre?" antes do POST criaria justamente a janela entre a pergunta e
 * a resposta em que a outra recepcionista marca. A gente tenta gravar,
 * e o 409 da constraint é a resposta — sempre correta, porque vem de
 * dentro da mesma transação.
 */

type Props = {
  inicio: Date
  recursoId?: string | null
  profissionalId?: string | null
  aoFechar(): void
  aoCriar(): void
}

const paraLocal = (d: Date) => {
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`
}

const brl = (centavos: number | string) =>
  (Number(centavos) / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

export function NovoAgendamento({
  inicio, recursoId, profissionalId, aoFechar, aoCriar,
}: Props) {
  const [quando, setQuando] = useState(() => paraLocal(inicio))
  const [paciente, setPaciente] = useState<Paciente | null>(null)
  const [procedimentoId, setProcedimentoId] = useState('')
  const [recurso, setRecurso] = useState(recursoId ?? '')
  const [profissional, setProfissional] = useState(profissionalId ?? '')
  const [observacao, setObservacao] = useState('')
  const [procedimentos, setProcedimentos] = useState<Procedimento[]>([])
  const [recursos, setRecursos] = useState<Recurso[]>([])
  const [profissionais, setProfissionais] = useState<Profissional[]>([])
  const [erro, setErro] = useState<string | null>(null)
  /* Quando o horário choca, a tela não vira parede: oferece o encaixe
     autorizado (dobrar de propósito), que a maioria dos concorrentes
     nem trava e nós travamos — mas com porta de saída, e auditada. */
  const [ofereceEncaixe, setOfereceEncaixe] = useState(false)
  const [enviando, setEnviando] = useState(false)

  /* ── o saldo de pacote deste paciente ────────────────────────────
     `POST /v1/agendamentos` aceita `pacote_sessao_id` e abate no MESMO
     commit desde a semana da agenda — e nenhuma tela mandava, porque
     não havia de onde tirar o id. O abatimento era código morto: o
     pacote era vendido e nunca descontava pela marcação.

     Carrega ao escolher o paciente, não antes: é a única hora em que a
     pergunta "ele tem pacote?" tem sujeito. */
  const [sessoes, setSessoes] = useState<SessaoDisponivel[]>([])
  const [sessaoId, setSessaoId] = useState('')

  useEffect(() => {
    setSessaoId('')
    if (!paciente) { setSessoes([]); return }
    const c = new AbortController()
    void (async () => {
      try {
        const r = await api<{ itens: SessaoDisponivel[] }>(
          `/v1/pacientes/${paciente.id}/sessoes-disponiveis`, { sinal: c.signal })
        setSessoes(r.itens)
        /* Um pacote só? Já vem marcado. É o caso da imensa maioria, e
           obrigar a escolher entre uma opção é clique sem decisão. Com
           dois ou mais, a pessoa escolhe — usar o pacote errado é
           dinheiro trocado de lugar. */
        if (r.itens.length === 1) setSessaoId(r.itens[0]!.pacote_sessao_id)
      } catch { setSessoes([]) }
    })()
    return () => c.abort()
  }, [paciente])

  /* Escolher a sessão do pacote AMARRA o procedimento, quando o pacote
     do catálogo aponta para um. Marcar "10 sessões de fisioterapia" e
     escolher "avaliação postural" no campo de cima é o erro que só
     aparece no fim do mês, no relatório. */
  const sessaoEscolhida = sessoes.find((x) => x.pacote_sessao_id === sessaoId) ?? null
  useEffect(() => {
    if (sessaoEscolhida?.procedimento_id) setProcedimentoId(sessaoEscolhida.procedimento_id)
  }, [sessaoEscolhida])

  useEffect(() => {
    void (async () => {
      try {
        const [pr, re, pf] = await Promise.all([
          api<Lista<Procedimento>>('/v1/procedimentos?limite=100'),
          api<Lista<Recurso>>('/v1/recursos?limite=100'),
          api<Lista<Profissional>>('/v1/profissionais?limite=100'),
        ])
        setProcedimentos(pr.itens.filter((x) => x.ativo))
        setRecursos(re.itens.filter((x) => x.ativo && x.agendavel))
        setProfissionais(pf.itens.filter((x) => x.ativo))
      } catch {
        setErro('Não foi possível carregar salas e procedimentos.')
      }
    })()
  }, [])

  const proc = useMemo(
    () => procedimentos.find((p) => p.id === procedimentoId) ?? null,
    [procedimentos, procedimentoId],
  )

  /* A pessoa precisa ver quando termina ANTES de salvar — é a metade da
     informação que ela usa para decidir se cabe. */
  const fimPrevisto = useMemo(() => {
    if (!proc) return null
    const d = new Date(quando)
    if (Number.isNaN(d.getTime())) return null
    return new Date(d.getTime() + proc.duracao_min * 60_000)
  }, [proc, quando])

  const podeSalvar =
    !!paciente && !!procedimentoId && (!!recurso || !!profissional) && !enviando

  async function submeter(e: React.FormEvent) {
    e.preventDefault()
    void marcar(false)
  }

  async function marcar(encaixe: boolean) {
    if (!paciente || !proc) return
    setErro(null); setOfereceEncaixe(false); setEnviando(true)
    try {
      await api('/v1/agendamentos', {
        metodo: 'POST',
        corpo: {
          paciente_id: paciente.id,
          procedimento_id: procedimentoId,
          inicio: new Date(quando).toISOString(),
          origem: encaixe ? 'ENCAIXE' : 'RECEPCAO',
          ...(encaixe ? { encaixe: true } : {}),
          ...(sessaoId ? { pacote_sessao_id: sessaoId } : {}),
          ...(observacao.trim() ? { observacao: observacao.trim() } : {}),
          alocacao: [{
            ...(recurso ? { recurso_id: recurso } : {}),
            ...(profissional ? { profissional_id: profissional } : {}),
            principal: true,
          }],
        },
      })
      aoCriar()
    } catch (err) {
      // Choque de horário não é erro do usuário — é a trava fazendo o
      // trabalho. Em vez de só recusar, oferece o encaixe autorizado.
      if (err instanceof ErroApi && err.codigo === 'CONFLITO_HORARIO') {
        setErro(err.message)
        setOfereceEncaixe(true)
      } else {
        setErro(err instanceof ErroApi ? err.message : 'Não foi possível marcar.')
      }
      setEnviando(false)
    }
  }

  return (
    <>
      <div className="painel__fundo" onClick={aoFechar} />
      <aside className="painel" role="dialog" aria-modal="true" aria-label="Novo atendimento">
        <header className="painel__topo">
          <h2>Novo atendimento</h2>
          <button className="botao botao--fantasma botao--pequeno" onClick={aoFechar}>
            Fechar
          </button>
        </header>

        <form className="formulario" onSubmit={submeter}>
          {erro && (
            <div className={`aviso ${ofereceEncaixe ? 'aviso--atencao' : 'aviso--erro'}`} role="alert">
              {/* Ao oferecer encaixe, NÃO repete o "escolha outro" da
                  mensagem crua — ele contradiz o "encaixar mesmo assim".
                  A porta de saída é um botão à parte: dobrar o horário é
                  decisão consciente, não o mesmo clique de "Marcar". */}
              {ofereceEncaixe ? (
                <div className="na__encaixe">
                  <span>Esse horário já está ocupado para essa sala ou profissional.
                        Encaixar mesmo assim dobra o atendimento de propósito — e
                        fica registrado quem autorizou.</span>
                  <button type="button" className="botao botao--pequeno"
                          disabled={enviando} onClick={() => void marcar(true)}>
                    Encaixar mesmo assim
                  </button>
                </div>
              ) : erro}
            </div>
          )}

          <BuscaPaciente escolhido={paciente} aoEscolher={setPaciente} />

          {/* ── o pacote, logo abaixo do paciente ──────────────────
              Este lugar não é arbitrário: é a primeira coisa que a
              recepção precisa saber DEPOIS de identificar quem é, e
              antes de escolher o procedimento — porque o pacote decide
              o procedimento, não o contrário. */}
          {sessoes.length > 0 && (
            <fieldset className="na__pacote">
              <legend>Este paciente tem pacote</legend>

              {sessoes.map((x) => {
                const marcado = sessaoId === x.pacote_sessao_id
                const usadas = x.contratadas - x.restantes
                return (
                  <label key={x.pacote_sessao_id}
                         className={`na__pac-op${marcado ? ' is-marcado' : ''}`}>
                    <input type="radio" name="na-pacote" checked={marcado}
                           onChange={() => setSessaoId(x.pacote_sessao_id)} />
                    <span className="na__pac-txt">
                      <strong>{x.nome}</strong>
                      <span className="ajuda">
                        Vai usar a <strong>{x.ordem}ª</strong> de {x.contratadas} —
                        {' '}sobram {x.restantes - 1} depois desta
                        {x.validade && ` · vale até ${new Date(x.validade).toLocaleDateString('pt-BR')}`}
                      </span>
                      {/* Proporção para o olho, número para a conferência.
                          A recepção precisa ver de longe quem está
                          acabando, para oferecer a renovação. */}
                      <span className="na__pac-barra" aria-hidden="true">
                        <span style={{ width: `${(usadas / x.contratadas) * 100}%` }} />
                      </span>
                    </span>
                  </label>
                )
              })}

              {/* Não usar o pacote é uma escolha legítima — sessão
                  avulsa, convênio, cortesia. Sem esta opção a pessoa
                  ficaria presa ao rádio que marcou. */}
              <label className={`na__pac-op${sessaoId === '' ? ' is-marcado' : ''}`}>
                <input type="radio" name="na-pacote" checked={sessaoId === ''}
                       onChange={() => setSessaoId('')} />
                <span className="na__pac-txt">
                  <strong>Não usar pacote desta vez</strong>
                  <span className="ajuda">
                    Cobra à parte e o saldo fica intacto.
                  </span>
                </span>
              </label>
            </fieldset>
          )}

          <div className="campo">
            <label htmlFor="na-proc">Procedimento</label>
            <select id="na-proc" className="entrada" required
                    value={procedimentoId}
                    disabled={!!sessaoEscolhida?.procedimento_id}
                    onChange={(e) => setProcedimentoId(e.target.value)}>
              <option value="">Escolha…</option>
              {procedimentos.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nome} · {p.duracao_min} min · {brl(p.valor_centavos)}
                </option>
              ))}
            </select>
            {sessaoEscolhida?.procedimento_id ? (
              <span className="ajuda">
                Definido pelo pacote "{sessaoEscolhida.nome}". Para escolher
                outro, desmarque o pacote acima.
              </span>
            ) : procedimentos.length === 0 && (
              <span className="ajuda">
                Nenhum procedimento cadastrado. Cadastre em A clínica › Catálogo.
              </span>
            )}
          </div>

          <div className="campo">
            <label htmlFor="na-quando">Início</label>
            <input id="na-quando" className="entrada" type="datetime-local" required
                   value={quando} onChange={(e) => setQuando(e.target.value)} />
            {fimPrevisto && (
              <span className="ajuda">
                Termina às{' '}
                <strong>
                  {fimPrevisto.toLocaleTimeString('pt-BR',
                    { hour: '2-digit', minute: '2-digit' })}
                </strong>
                {proc && proc.limpeza_min > 0 &&
                  ` · a sala fica ocupada mais ${proc.limpeza_min} min para limpeza`}
              </span>
            )}
          </div>

          <div className="na__dupla">
            <div className="campo">
              <label htmlFor="na-sala">Sala</label>
              <select id="na-sala" className="entrada"
                      value={recurso} onChange={(e) => setRecurso(e.target.value)}>
                <option value="">Sem sala</option>
                {recursos.map((r) => (
                  <option key={r.id} value={r.id}>{r.nome}</option>
                ))}
              </select>
            </div>

            <div className="campo">
              <label htmlFor="na-prof">Profissional</label>
              <select id="na-prof" className="entrada"
                      value={profissional} onChange={(e) => setProfissional(e.target.value)}>
                <option value="">Sem profissional</option>
                {profissionais.map((p) => (
                  <option key={p.id} value={p.id}>{p.nome_curto ?? p.nome}</option>
                ))}
              </select>
            </div>
          </div>

          {!recurso && !profissional && (
            <span className="ajuda">
              Escolha ao menos uma sala ou um profissional — é o que reserva o horário.
            </span>
          )}

          <div className="campo">
            <label htmlFor="na-obs">Observação</label>
            <textarea id="na-obs" className="entrada" rows={2}
                      placeholder="Visível para a equipe na agenda"
                      value={observacao} onChange={(e) => setObservacao(e.target.value)} />
          </div>

          <button className="botao" type="submit" disabled={!podeSalvar}>
            {enviando ? 'Marcando…' : 'Marcar atendimento'}
          </button>
        </form>
      </aside>
    </>
  )
}

/* ══ busca de paciente ════════════════════════════════════════════ */

/**
 * Autocomplete com criação embutida.
 *
 * O paciente novo que liga pela primeira vez é o caso mais comum na
 * recepção. Obrigar a sair da tela, cadastrar e voltar é o que faz a
 * atendente anotar no papel "para cadastrar depois" — e o depois não
 * chega.
 */
function BuscaPaciente({
  escolhido, aoEscolher,
}: {
  escolhido: Paciente | null
  aoEscolher(p: Paciente | null): void
}) {
  const [termo, setTermo] = useState('')
  const [achados, setAchados] = useState<Paciente[]>([])
  const [aberto, setAberto] = useState(false)
  const [criando, setCriando] = useState(false)
  const timer = useRef<number | null>(null)

  useEffect(() => {
    if (escolhido || termo.trim().length < 2) { setAchados([]); return }
    const c = new AbortController()
    if (timer.current) window.clearTimeout(timer.current)
    timer.current = window.setTimeout(async () => {
      try {
        const q = new URLSearchParams({ busca: termo.trim(), limite: '8' })
        const r = await api<Lista<Paciente>>(`/v1/pacientes?${q}`, { sinal: c.signal })
        setAchados(r.itens); setAberto(true)
      } catch { /* busca sem resultado não é erro que mereça alarde */ }
    }, 250)
    return () => { c.abort(); if (timer.current) window.clearTimeout(timer.current) }
  }, [termo, escolhido])

  async function criarRapido() {
    setCriando(true)
    try {
      const p = await api<Paciente>('/v1/pacientes', {
        metodo: 'POST', corpo: { nome: termo.trim() },
      })
      aoEscolher(p); setAberto(false)
    } catch { /* o erro aparece no formulário de cima ao salvar */ } finally {
      setCriando(false)
    }
  }

  if (escolhido) {
    return (
      <div className="campo">
        <label>Paciente</label>
        <div className="na__escolhido">
          <span>
            <strong>{escolhido.nome_social ?? escolhido.nome}</strong>
            {escolhido.telefone && <span className="ajuda"> · {escolhido.telefone}</span>}
          </span>
          <button type="button" className="botao botao--fantasma botao--pequeno"
                  onClick={() => { aoEscolher(null); setTermo('') }}>Trocar</button>
        </div>
        {escolhido.financeiro === 'VENCIDO' && (
          <span className="ajuda ajuda--erro">Este paciente tem pendência vencida.</span>
        )}
      </div>
    )
  }

  return (
    <div className="campo na__busca">
      <label htmlFor="na-pac">Paciente</label>
      <input id="na-pac" className="entrada" autoComplete="off" autoFocus
             placeholder="Nome, telefone ou CPF"
             value={termo}
             onChange={(e) => setTermo(e.target.value)}
             onFocus={() => setAberto(true)} />

      {aberto && termo.trim().length >= 2 && (
        <ul className="na__lista" role="listbox">
          {achados.map((p) => (
            <li key={p.id}>
              <button type="button" className="na__item"
                      onClick={() => { aoEscolher(p); setAberto(false) }}>
                <span>{p.nome_social ?? p.nome}</span>
                <span className="ajuda">{p.telefone ?? 'sem telefone'}</span>
              </button>
            </li>
          ))}
          {achados.length === 0 && (
            <li>
              <button type="button" className="na__item na__item--novo"
                      onClick={() => void criarRapido()} disabled={criando}>
                {criando
                  ? 'Cadastrando…'
                  : <>Cadastrar <strong>{termo.trim()}</strong> como paciente novo</>}
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  )
}
